import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import JobFilePopupData from './JobFilenamePopup';
import ConfirmPopup from './ConfirmPopup';
import Pager from './Paging';

interface JobFilesProps {
    jobId: number;
}

interface JobFiles {
    id: number;
    dateFormatted: string;
    fileName: string;
    fileType: string;
    fileTypeId: number;
    showInApp: boolean;
    addedBy: string;
}

interface GenericList {
    id: number;
    name: string;
}

const JobFilesData = (props: JobFilesProps) => {
    const [loading, setLoading] = React.useState(true);
    const [files, setFiles] = React.useState<JobFiles[]>([]);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);
    const [showConfirmModal, setShowConfirm] = React.useState(false);
    const [modalSaveDisabled, setModalSaveDisabled] = React.useState(false);
    const [showFilenameModal, setShowFilenameModal] = React.useState(false);
    const [fileId, setFileId] = React.useState(0);
    const [fileType, setFileType] = React.useState("");
    const [pageDetails, setPageDetails] = React.useState({ totalRecords: 0, pageSize: 10 });
    const [page, setPage] = React.useState(1);
    const [fileTypes, setFileTypes] = React.useState<GenericList[]>([]);
    const [typeId, setTypeId] = React.useState(-1);
    const [showAppFiles, setShowAppFiles] = React.useState(false);
    const [showAddFiles, setShowAddFiles] = React.useState(false);
    const [importTypeId, setImportTypeId] = React.useState(-1);
    const [importFiles, setImportFiles] = React.useState<File[]>([]);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetFileTypes', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setFileTypes(res.data);
            getFiles(1, true, -1, false);
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const getFiles = async (pageNumber: number, getTotal: boolean, fileTypeId: number, showInApp: boolean) => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetJobFiles?jobId=' + props.jobId + '&page=' + pageNumber + '&totalRecords=' + pageDetails.totalRecords + '&getTotalRecords=' + getTotal + '&showAppFiles=' + showInApp + '&typeId=' + fileTypeId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setFiles(res.data.jobFiles);
            if (getTotal) {
                setPageDetails({ totalRecords: res.data.totalRecords, pageSize: res.data.pageSize });
            }
            setLoading(false);
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const pageChanged = (page: number) => {
        setPage(page);
        getFiles(page, false, typeId, showAppFiles);
    }

    const handleShowInAppChange = (e: any) => {
        setShowAppFiles(e.target.checked);
        getFiles(1, true, typeId, e.target.checked);
    }

    const handleTypeChange = (e: any) => {
        let fileTypeId = e.target.value * 1;
        setTypeId(fileTypeId);
        getFiles(1, true, fileTypeId, showAppFiles);
    }

    const handleImportTypeChange = (e: any) => {
        delete errors["importTypeId"];
        let fileTypeId = e.target.value * 1;
        setImportTypeId(fileTypeId);
    }

    const onCellCheckboxChange = (e: any, index: number) => {
        saveShowInApp(index, e.target.checked);
    }

    const saveShowInApp = async (index: number, showInApp: boolean) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobFiles = [...files];
        var file = jobFiles[index];
        file.showInApp = showInApp;

        var data = {
            id: file.id,
            showInApp: showInApp,
            subId: user.sub
        };

        axios.post('api/Job/UpdateJobFileShowInApp', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                setFiles(jobFiles);
                toast.success("File has been updated");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    const onCellChange = (e: any, index: number) => {
        saveFileType(index, e.target.value * 1);
    }

    const saveFileType = async (index: number, fileTypeId: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobFiles = [...files];
        var file = jobFiles[index];
        file.fileTypeId = fileTypeId;

        var data = {
            id: file.id,
            fileTypeId: fileTypeId,
            subId: user.sub
        };

        axios.post('api/Job/UpdateJobFileType', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                setFiles(jobFiles);
                toast.success("File has been updated");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    //IMPORT
    const addNewFiles = () => {
        setShowAddFiles(true);
    }

    const hideAddNewFiles = () => {
        setShowAddFiles(false);
    }

    const handleImportFile = (e: any) => {
        delete errors["importFiles"];
        setImportFiles(e.target.files);
    }

    const validate = () => {
        let error: any = {};
        let formIsValid = true;

        if (importTypeId < 1) {
            error["importTypeId"] = "File Type is required";
            formIsValid = false;
        }

        if (importFiles.length === 0) {
            error["importFiles"] = "Select one or more files to upload!";
            formIsValid = false;
        }

        setErrors(error);
        return formIsValid;
    }

    const importJobFiles = () => {
        if (validate()) {
            saveJobFiles();
        } else {
            toast.error("Fix validation errors before saving files!");
        }
    }

    const saveJobFiles = async () => {
        //save photos
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        const data = new FormData()
        data.append('jobId', props.jobId.toString());
        data.append('fileTypeId', importTypeId.toString());
        data.append('subId', user.sub);

        for (var i = 0; i < importFiles.length; i++) {
            data.append('files', importFiles[i]);
        }

        //save photos
        axios.post('api/Job/SaveJobFiles', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setPage(1);
            setImportTypeId(-1);
            setImportFiles([]);
            setShowAddFiles(false);
            toast.success("Files have been uploaded");
            getFiles(1, true, typeId, showAppFiles);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END IMPORT

    //UPDATE FILENAME
    const updateFilename = (e: any, itemIndex: number) => {
        e.preventDefault();

        var file = files[itemIndex];
        var filename = file.fileName;
        var dotIndex = filename.lastIndexOf('.');
        var filetype = ".pdf";  //default to pdf if no filetype
        if (dotIndex >= 0) {
            filetype = filename.substr(dotIndex);
        }

        setFileId(file.id);
        setFileType(filetype);
        setShowFilenameModal(true);
    }

    const hideUpdateFilename = () => {
        setFileId(0);
        setFileType("");
        setShowFilenameModal(false);
    }

    const saveUpdateFilename = () => {
        setFileId(0);
        setShowFilenameModal(false);
        setFileType("");
        getFiles(page, false, typeId, showAppFiles);
    }
    //END UPDATE FILENAME

    //DELETE
    const deleteFile = (e: any, itemIndex: number) => {
        e.preventDefault();
        setDeleteIndex(itemIndex);
        setShowConfirm(true);
        setModalSaveDisabled(false);
    }

    const hideConfirmModal = () => {
        setDeleteIndex(-1);
        setShowConfirm(false);
        setModalSaveDisabled(false);
    }

    const saveConfirmModal = () => {
        if (modalSaveDisabled) {
            return;
        }

        deleteJobFile();
    }

    const deleteJobFile = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var jobFiles = [...files];
        var file = jobFiles[deleteIndex];

        var data = {
            id: file.id,
            subId: user.sub
        };

        //delete photo
        axios.post('api/Job/DeleteJobFile', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                jobFiles.splice(deleteIndex, 1);
                setFiles(jobFiles);
                setDeleteIndex(-1);
                setShowConfirm(false);
                setModalSaveDisabled(true);
                toast.success("File has been deleted");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });

    }
    //END DELETE

    //DOWNLOAD FILE
    //download job plan
    const downloadFile = (e: any, itemIndex: number) => {
        e.preventDefault();

        var file = files[itemIndex];

        downloadJobFile(file.id);
    }

    const downloadJobFile = async (id: number) => {
        const token = await authService.getAccessToken();

        axios('api/Job/GetJobPlans?Id=' + id, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` },
            method: 'GET',
            responseType: 'blob' //Force to receive data in a Blob Format
        })
        .then(res => {
            //DOWNLOAD FILE TO BROWSER
            var fileheader = res.headers["content-disposition"];
            var filename = "jobfile.pdf";
            if (fileheader) {
                filename = fileheader.split('filename=')[1].split(';')[0];
                filename = filename.replaceAll('"', '');
            }
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END DOWNLOAD FILE

    const confirmPopup = <ConfirmPopup heading="Delete File?" text="Are you sure you want to delete this file?" hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />
    const filenamePopup = <JobFilePopupData fileId={fileId} fileType={fileType} showModal={showFilenameModal} hideModal={hideUpdateFilename} saveModal={saveUpdateFilename} />

    var pagerContents;
    if ((pageDetails.totalRecords / pageDetails.pageSize) > 1) {
        //only render if we have records
        pagerContents = <Pager page={page} pageSize={pageDetails.pageSize} totalRecords={pageDetails.totalRecords} pageChanged={pageChanged} />
    }

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
                {filenamePopup}
            </div>
            <div className={showAddFiles ? "hidden" : ""}>
                <button className="defaultbutton defaultbutton__small form-margin" onClick={addNewFiles} type="button">Add Files</button>
            </div>
            <div className={showAddFiles ? "" : "hidden" }>
                <h3>Add New File/s</h3>
                <div>
                    <label className="input-group" htmlFor="importTypeId">
                        <span className="label">File Type </span>
                        <select className="select" id="importTypeId" name="importTypeId" value={importTypeId} onChange={(e) => handleImportTypeChange(e)}>
                            <option defaultValue="-1"></option>
                            {fileTypes.map(type =>
                                <option key={type.id} value={type.id}>{type.name}</option>
                            )};
                        </select>
                    </label>
                    <span className={errors["importTypeId"] ? "label errors" : "hidden"}>{errors["importTypeId"]}</span>
                </div>
                <div className="marginTop10">
                    <label className="input-group" htmlFor="file">
                        <span className="label">Select Files </span>
                        <input type="file" className="marginBottom10" name="file" accept="application/pdf" onChange={handleImportFile} multiple />
                    </label>
                    <span className={errors["importFiles"] ? "label errors" : "hidden"}>{errors["importFiles"]}</span>
                </div>
                <button className="defaultbutton-prev" onClick={importJobFiles} type="button">Save Files</button>
                <button className="defaultbutton-next" onClick={hideAddNewFiles} type="button">Cancel</button>
            </div>
            <hr></hr>
            <div className="makeitflex">
                <label className="input-group--inline" htmlFor="typeId">
                    <span className="label">Type </span>
                    <select className="select" id="typeId" name="typeId" value={typeId} onChange={(e) => handleTypeChange(e)}>
                        <option defaultValue="-1"></option>
                        {fileTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <label className="input-group--inline job__checkboxPosition" htmlFor="showAppFiles">
                    <span className="label">Show App Files ONLY </span>
                    <div>
                        <input type="checkbox" name="showAppFiles" id="showAppFiles" checked={showAppFiles} onChange={(e) => handleShowInAppChange(e)}></input>
                    </div>
                </label>
            </div>
            <div className="job__noteGridSize overflowAuto">
                <table className="table tableColours">
                    <thead>
                        <tr>
                            <th>Date/Time</th>
                            <th></th>
                            <th>Filename</th>
                            <th>File Type</th>
                            <th className="textaligncenter">Show in App</th>
                            <th>Added By</th>
                            <th></th>
                        </tr>
                    </thead>
                    {files.map((file, index) =>
                        <tbody key={file.id}>
                            <tr>
                                <td className="table__text--align">{file.dateFormatted}</td>
                                <td className="table__text--align">
                                    <a className="tooltipitem tooltipitem__right alignIconCenter" href="#" onClick={(e) => updateFilename(e, index)}>
                                        <span className="fas fa-edit copy--icon"></span>
                                        <span className="tooltipitemtext tooltipitemtext__right">Update filename</span>
                                    </a>
                                </td>
                                <td className="table__text--align">
                                    <a href="#" onClick={(e) => downloadFile(e, index)}>{file.fileName}</a>
                                </td>
                                <td className="table__text--align">
                                    <select className="select" id="fileTypeId" name="fileTypeId" value={file.fileTypeId} onChange={(e) => onCellChange(e, index)}>
                                        <option hidden defaultValue="-1"></option>
                                        {fileTypes.map(type =>
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className="table__text--align">
                                    <input type='checkbox' id="selected" name="selected" className='alignIconCenter' checked={file.showInApp} onChange={(e) => onCellCheckboxChange(e, index)} />
                                </td>
                                <td className="table__text--align">{file.addedBy}</td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteFile(e, index)}>
                                            <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                        </a>
                                    </div> 
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            {pagerContents}
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobFilesData;